import { supabase } from '../lib/supabase';
import { userAnalyticsService } from './userAnalyticsService';
import { dashboardCustomizationService } from './dashboardCustomizationService';

// In-memory cache with TTL and usage tracking
const cache = new Map();
const cacheMetadata = new Map();
const CACHE_STATS = {
  hits: 0,
  misses: 0,
  evictions: 0,
  totalRequests: 0
};

// Configuration
const CACHE_CONFIG = {
  DEFAULT_TTL: 5 * 60 * 1000, // 5 minutes
  FREQUENT_ACCESS_TTL: 15 * 60 * 1000, // 15 minutes for frequently accessed
  MAX_CACHE_SIZE: 100,
  FREQUENT_ACCESS_THRESHOLD: 5, // Access count to be considered frequent
  USAGE_PATTERN_WINDOW: 7 * 24 * 60 * 60 * 1000, // 7 days
  AUTO_WARM_INTERVAL: 10 * 60 * 1000 // 10 minutes
};

export const widgetCacheService = {
  // ============================================================================
  // CORE CACHE OPERATIONS
  // ============================================================================

  /**
   * Get data from cache or fetch from source
   */
  async get(key, fetchFunction, options = {}) {
    CACHE_STATS.totalRequests++;

    const cached = cache?.get(key);
    const metadata = cacheMetadata?.get(key);

    // Check if cache is valid
    if (cached && metadata && Date.now() < metadata?.expiresAt) {
      CACHE_STATS.hits++;
      this.updateAccessPattern(key);
      return cached;
    }

    // Cache miss - fetch fresh data
    CACHE_STATS.misses++;
    try {
      const data = await fetchFunction();
      this.set(key, data, options);
      return data;
    } catch (error) {
      console.error(`Cache fetch error for key ${key}:`, error);
      // Return stale cache if available on error
      if (cached) {
        console.warn(`Returning stale cache for key ${key}`);
        return cached;
      }
      throw error;
    }
  },

  /**
   * Set data in cache with TTL
   */
  set(key, data, options = {}) {
    const metadata = cacheMetadata?.get(key) || { accessCount: 0, lastAccessed: Date.now() };
    
    // Determine TTL based on access patterns
    const isFrequentlyAccessed = metadata?.accessCount >= CACHE_CONFIG?.FREQUENT_ACCESS_THRESHOLD;
    const ttl = options?.ttl || (isFrequentlyAccessed ? CACHE_CONFIG?.FREQUENT_ACCESS_TTL : CACHE_CONFIG?.DEFAULT_TTL);

    cache?.set(key, data);
    cacheMetadata?.set(key, {
      ...metadata,
      expiresAt: Date.now() + ttl,
      cachedAt: Date.now(),
      size: JSON.stringify(data)?.length
    });

    // Enforce cache size limit
    this.enforceMaxSize();

    // Track cache operation
    this.trackCacheOperation(key, 'set', data);
  },

  /**
   * Invalidate specific cache entry
   */
  invalidate(key) {
    const existed = cache?.has(key);
    cache?.delete(key);
    cacheMetadata?.delete(key);
    
    if (existed) {
      this.trackCacheOperation(key, 'invalidate');
    }
    
    return existed;
  },

  /**
   * Invalidate cache entries by pattern
   */
  invalidatePattern(pattern) {
    const regex = new RegExp(pattern);
    const keysToDelete = [];

    for (const key of cache?.keys()) {
      if (regex?.test(key)) {
        keysToDelete?.push(key);
      }
    }

    keysToDelete?.forEach(key => this.invalidate(key));
    return keysToDelete?.length;
  },

  /**
   * Clear all cache
   */
  clear() {
    const size = cache?.size;
    cache?.clear();
    cacheMetadata?.clear();
    this.trackCacheOperation('*', 'clear');
    return size;
  },

  // ============================================================================
  // USAGE PATTERN DETECTION
  // ============================================================================

  /**
   * Update access pattern for a cache key
   */
  updateAccessPattern(key) {
    const metadata = cacheMetadata?.get(key);
    if (metadata) {
      metadata.accessCount = (metadata?.accessCount || 0) + 1;
      metadata.lastAccessed = Date.now();
      cacheMetadata?.set(key, metadata);
    }
  },

  /**
   * Analyze widget usage patterns from analytics data
   */
  async analyzeUsagePatterns(userEmail = null) {
    try {
      const dateFrom = new Date(Date.now() - CACHE_CONFIG.USAGE_PATTERN_WINDOW)?.toISOString();
      const dateTo = new Date()?.toISOString();

      // Fetch data with individual error handling to prevent complete failure
      let pageViews = [];
      let featureUsage = [];
      let widgetPreferences = [];

      try {
        pageViews = await userAnalyticsService?.getPageViews({ dateFrom, dateTo, userEmail }) || [];
      } catch (error) {
        console.warn('Failed to fetch page views for usage analysis:', error?.message);
        pageViews = [];
      }

      try {
        featureUsage = await userAnalyticsService?.getFeatureUsage({ dateFrom, dateTo, userEmail }) || [];
      } catch (error) {
        console.warn('Failed to fetch feature usage for usage analysis:', error?.message);
        featureUsage = [];
      }

      try {
        widgetPreferences = userEmail ? await dashboardCustomizationService?.getUserWidgetPreferences(userEmail) : [];
      } catch (error) {
        console.warn('Failed to fetch widget preferences for usage analysis:', error?.message);
        widgetPreferences = [];
      }

      // Aggregate access patterns
      const patterns = {};

      // Analyze page views
      pageViews?.forEach(view => {
        const key = `page:${view?.pagePath}`;
        patterns[key] = patterns?.[key] || { type: 'page', identifier: view?.pagePath, accessCount: 0, avgTimeSpent: 0, totalTimeSpent: 0 };
        patterns[key].accessCount++;
        patterns[key].totalTimeSpent += view?.timeSpentSeconds || 0;
      });

      // Analyze feature usage
      featureUsage?.forEach(usage => {
        const key = `feature:${usage?.featureName}`;
        patterns[key] = patterns?.[key] || { type: 'feature', identifier: usage?.featureName, accessCount: 0, successRate: 0, totalAttempts: 0, successfulAttempts: 0 };
        patterns[key].accessCount++;
        patterns[key].totalAttempts++;
        if (usage?.success) patterns[key].successfulAttempts++;
      });

      // Calculate averages and success rates
      Object.values(patterns)?.forEach(pattern => {
        if (pattern?.type === 'page' && pattern?.accessCount > 0) {
          pattern.avgTimeSpent = Math.round(pattern?.totalTimeSpent / pattern?.accessCount);
        }
        if (pattern?.type === 'feature' && pattern?.totalAttempts > 0) {
          pattern.successRate = ((pattern?.successfulAttempts / pattern?.totalAttempts) * 100)?.toFixed(1);
        }
      });

      // Sort by access count to identify frequently accessed items
      const sortedPatterns = Object.entries(patterns)?.map(([key, data]) => ({ key, ...data }))?.sort((a, b) => b?.accessCount - a?.accessCount);

      return {
        patterns: sortedPatterns,
        frequentlyAccessed: sortedPatterns?.filter(p => p?.accessCount >= CACHE_CONFIG?.FREQUENT_ACCESS_THRESHOLD),
        totalAnalyzed: sortedPatterns?.length,
        analysisWindow: CACHE_CONFIG?.USAGE_PATTERN_WINDOW
      };
    } catch (error) {
      console.warn('Error analyzing usage patterns:', error?.message || error);
      // Return empty results instead of throwing to prevent cascade failures
      return {
        patterns: [],
        frequentlyAccessed: [],
        totalAnalyzed: 0,
        analysisWindow: CACHE_CONFIG?.USAGE_PATTERN_WINDOW
      };
    }
  },

  /**
   * Get frequently accessed widgets for a user or role
   */
  async getFrequentlyAccessedWidgets(userEmail = null, role = null) {
    try {
      const { patterns } = await this.analyzeUsagePatterns(userEmail);
      
      // Filter for widget-related patterns
      const widgetPatterns = patterns?.filter(p => 
        p?.type === 'page' && (p?.identifier?.includes('dashboard') || p?.identifier?.includes('widget'))
      );

      return widgetPatterns?.filter(p => p?.accessCount >= CACHE_CONFIG?.FREQUENT_ACCESS_THRESHOLD)?.slice(0, 10); // Top 10 frequently accessed
    } catch (error) {
      console.warn('Error getting frequently accessed widgets:', error?.message || error);
      return [];
    }
  },

  // ============================================================================
  // AUTOMATIC CACHE WARMING
  // ============================================================================

  /**
   * Warm cache for frequently accessed widgets
   */
  async warmCache(userEmail = null) {
    try {
      const frequentWidgets = await this.getFrequentlyAccessedWidgets(userEmail);
      
      const warmingPromises = frequentWidgets?.map(async (widget) => {
        const cacheKey = `widget:${widget?.identifier}`;
        
        // Skip if already cached and valid
        const metadata = cacheMetadata?.get(cacheKey);
        if (metadata && Date.now() < metadata?.expiresAt) {
          return { key: cacheKey, status: 'already_cached' };
        }

        try {
          // Fetch and cache widget data
          const widgetData = await dashboardCustomizationService?.getVisibleWidgets();
          this.set(cacheKey, widgetData, { ttl: CACHE_CONFIG?.FREQUENT_ACCESS_TTL });
          return { key: cacheKey, status: 'warmed' };
        } catch (error) {
          console.error(`Failed to warm cache for ${cacheKey}:`, error);
          return { key: cacheKey, status: 'failed', error: error?.message };
        }
      });

      const results = await Promise.allSettled(warmingPromises);
      const warmed = results?.filter(r => r?.status === 'fulfilled' && r?.value?.status === 'warmed')?.length;
      
      console.log(`Cache warming completed: ${warmed} widgets warmed`);
      return { warmed, total: frequentWidgets?.length, results };
    } catch (error) {
      console.error('Error warming cache:', error);
      throw error;
    }
  },

  /**
   * Start automatic cache warming interval
   */
  startAutoWarmInterval(userEmail = null) {
    if (this._warmInterval) {
      clearInterval(this._warmInterval);
    }

    this._warmInterval = setInterval(() => {
      this.warmCache(userEmail)?.catch(error => {
        console.error('Auto warm cache failed:', error);
      });
    }, CACHE_CONFIG?.AUTO_WARM_INTERVAL);

    // Initial warm
    this.warmCache(userEmail);

    console.log('Auto cache warming started');
  },

  /**
   * Stop automatic cache warming
   */
  stopAutoWarmInterval() {
    if (this._warmInterval) {
      clearInterval(this._warmInterval);
      this._warmInterval = null;
      console.log('Auto cache warming stopped');
    }
  },

  // ============================================================================
  // CACHE MANAGEMENT
  // ============================================================================

  /**
   * Enforce maximum cache size by evicting least recently used entries
   */
  enforceMaxSize() {
    if (cache?.size <= CACHE_CONFIG?.MAX_CACHE_SIZE) return;

    // Sort by last accessed time
    const entries = Array.from(cacheMetadata?.entries())?.sort((a, b) => a?.[1]?.lastAccessed - b?.[1]?.lastAccessed);

    // Remove oldest entries
    const toRemove = cache?.size - CACHE_CONFIG?.MAX_CACHE_SIZE;
    for (let i = 0; i < toRemove; i++) {
      const [key] = entries?.[i];
      cache?.delete(key);
      cacheMetadata?.delete(key);
      CACHE_STATS.evictions++;
    }
  },

  /**
   * Get cache statistics
   */
  getStatistics() {
    const hitRate = CACHE_STATS?.totalRequests > 0
      ? ((CACHE_STATS?.hits / CACHE_STATS?.totalRequests) * 100)?.toFixed(2)
      : 0;

    const totalSize = Array.from(cacheMetadata?.values())?.reduce((sum, meta) => sum + (meta.size || 0), 0);

    const avgAccessCount = Array.from(cacheMetadata?.values())?.reduce((sum, meta) => sum + (meta.accessCount || 0), 0) / Math.max(1, cacheMetadata?.size);

    return {
      ...CACHE_STATS,
      hitRate: parseFloat(hitRate),
      currentSize: cache?.size,
      maxSize: CACHE_CONFIG?.MAX_CACHE_SIZE,
      totalSizeBytes: totalSize,
      avgAccessCount: avgAccessCount?.toFixed(2),
      config: CACHE_CONFIG
    };
  },

  /**
   * Get cache entries metadata
   */
  getCacheEntries() {
    return Array.from(cacheMetadata?.entries())?.map(([key, metadata]) => ({
      key,
      ...metadata,
      isExpired: Date.now() >= metadata?.expiresAt,
      ttlRemaining: Math.max(0, metadata?.expiresAt - Date.now()),
      age: Date.now() - metadata?.cachedAt
    }));
  },

  /**
   * Track cache operations in analytics
   */
  async trackCacheOperation(key, operation, data = null) {
    try {
      await supabase?.from('feature_usage')?.insert({
        feature_category: 'cache',
        feature_name: 'widget_cache',
        action_type: operation,
        success: true,
        used_at: new Date()?.toISOString()
      });
    } catch (error) {
      // Silent fail - don't break cache operations
      console.debug('Cache tracking failed:', error);
    }
  },

  // ============================================================================
  // WIDGET-SPECIFIC CACHE HELPERS
  // ============================================================================

  /**
   * Get cached widget data with automatic fetching
   */
  async getWidgetData(widgetId, fetchFunction) {
    const cacheKey = `widget:data:${widgetId}`;
    return this.get(cacheKey, fetchFunction);
  },

  /**
   * Get cached widget preferences
   */
  async getUserWidgetPreferences(userEmail) {
    const cacheKey = `widget:preferences:${userEmail}`;
    return this.get(cacheKey, () => 
      dashboardCustomizationService?.getUserWidgetPreferences(userEmail)
    );
  },

  /**
   * Invalidate widget-related caches
   */
  invalidateWidgetCaches(widgetId = null) {
    if (widgetId) {
      return this.invalidatePattern(`widget:.*:${widgetId}`);
    }
    return this.invalidatePattern('widget:.*');
  },

  /**
   * Get cache performance metrics for analytics dashboard
   */
  async getCachePerformanceMetrics() {
    const stats = this.getStatistics();
    const entries = this.getCacheEntries();

    // Calculate additional metrics
    const expiredEntries = entries?.filter(e => e?.isExpired)?.length;
    const frequentlyAccessed = entries?.filter(e => e?.accessCount >= CACHE_CONFIG?.FREQUENT_ACCESS_THRESHOLD)?.length;
    const avgTTLRemaining = entries?.reduce((sum, e) => sum + e?.ttlRemaining, 0) / Math.max(1, entries?.length);

    return {
      ...stats,
      expiredEntries,
      frequentlyAccessedCount: frequentlyAccessed,
      avgTTLRemainingMs: Math.round(avgTTLRemaining),
      cacheEfficiency: stats?.hitRate,
      memoryUsageMB: (stats?.totalSizeBytes / (1024 * 1024))?.toFixed(2)
    };
  }
};

// Auto-start cache warming on module load (can be disabled if needed)
if (typeof window !== 'undefined') {
  // Start after a short delay to allow app initialization
  setTimeout(() => {
    widgetCacheService?.startAutoWarmInterval();
  }, 5000);
}

export default widgetCacheService;
